import React, { useEffect, useState } from "react";
import { Download, Share, Plus, X, Smartphone, Monitor, QrCode } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

/**
 * InstallAppButton — cross-platform "Add EVIQ to your home screen" affordance.
 *
 * Behaviour matrix:
 *   • Chrome / Edge / Android w/ deferred prompt  → fires the native install dialog
 *   • iOS Safari                                   → opens Share → Add to Home Screen guide
 *   • Desktop Safari / Firefox / others            → opens a "Install on your phone" QR modal
 *   • Already installed (display-mode: standalone) → hidden
 *
 * The button always renders (unless already installed) so users know EVIQ
 * is installable without needing the app store.
 */
export const InstallAppButton = ({ variant = "pill", className = "" }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [modal, setModal] = useState(null); // null | "ios" | "desktop"

  useEffect(() => {
    const ua = navigator.userAgent || "";
    const ios = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const android = /Android/i.test(ua);
    setIsIOS(ios);
    setIsAndroid(android);

    const standalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    setIsStandalone(standalone);

    // Prompt is captured globally in index.js before React mounts.
    setDeferredPrompt(window.__eviqInstallPrompt || null);
    const onReady = () => setDeferredPrompt(window.__eviqInstallPrompt || null);
    const onInstalled = () => setIsStandalone(true);
    window.addEventListener("eviq:install-ready", onReady);
    window.addEventListener("eviq:app-installed", onInstalled);
    return () => {
      window.removeEventListener("eviq:install-ready", onReady);
      window.removeEventListener("eviq:app-installed", onInstalled);
    };
  }, []);

  // Hide when already installed — nothing to do
  if (isStandalone) return null;

  const doInstall = async () => {
    // Chrome / Edge / Android with the native prompt available
    const prompt = deferredPrompt || window.__eviqInstallPrompt;
    if (prompt) {
      prompt.prompt();
      const { outcome } = await prompt.userChoice;
      window.__eviqInstallPrompt = null;
      setDeferredPrompt(null);
      if (outcome === "accepted") setIsStandalone(true);
      return;
    }
    // iOS Safari → show instructions modal
    if (isIOS) { setModal("ios"); return; }
    // Everything else (desktop Safari/Firefox, Chrome pre-engagement, iOS Chrome…)
    // → show the QR-code "open on your phone" modal
    setModal("desktop");
  };

  const label = isIOS
    ? "Add to Home Screen"
    : isAndroid || deferredPrompt
    ? "Install app"
    : "Get the app";

  const styles =
    variant === "link"
      ? "text-xs text-zinc-400 hover:text-white transition-colors inline-flex items-center gap-1.5"
      : "inline-flex items-center gap-1.5 rounded-full border border-[#39FF14]/40 hover:border-[#39FF14] bg-[#39FF14]/[0.08] hover:bg-[#39FF14]/[0.14] px-3 py-1.5 text-xs font-semibold text-[#39FF14] transition-colors";

  return (
    <>
      <button
        type="button"
        onClick={doInstall}
        data-testid="install-app-button"
        className={`${styles} ${className}`}
        aria-label="Install EVIQ to your home screen"
      >
        <Download className="w-3.5 h-3.5" strokeWidth={2.4} aria-hidden="true" />
        {label}
      </button>

      {modal === "ios" && <IOSInstallHelp onClose={() => setModal(null)} />}
      {modal === "desktop" && <DesktopInstallHelp onClose={() => setModal(null)} />}
    </>
  );
};

/** iOS instructions modal — the only way to "install" a PWA on iOS Safari. */
const IOSInstallHelp = ({ onClose }) => (
  <div
    className="fixed inset-0 z-[110] bg-black/90 backdrop-blur-md flex items-center justify-center px-5"
    data-testid="ios-install-help"
    role="dialog"
    aria-modal="true"
  >
    <div className="relative w-full max-w-sm rounded-3xl border border-white/10 bg-[#0a0a0a] p-6 text-white">
      <button
        onClick={onClose}
        aria-label="Close"
        data-testid="ios-install-close"
        className="absolute top-3 right-3 text-zinc-500 hover:text-white p-2 rounded-full border border-white/10 hover:border-white/25 transition-colors"
      >
        <X className="w-3.5 h-3.5" strokeWidth={2} aria-hidden="true" />
      </button>

      <div className="flex items-start gap-3 mb-5">
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
          style={{ background: "rgba(0,229,255,0.1)", border: "1px solid rgba(0,229,255,0.35)" }}
          aria-hidden="true"
        >
          <Smartphone className="w-5 h-5" style={{ color: "#00E5FF" }} strokeWidth={2} />
        </div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-1">
            Two-tap install
          </div>
          <h3 className="text-lg font-semibold leading-tight">
            Add EVIQ to your iPhone home screen
          </h3>
        </div>
      </div>

      <ol className="space-y-3 mb-5">
        <Step n="1">
          Tap the <strong className="text-white">Share</strong> icon at the bottom of
          Safari{" "}
          <span
            className="inline-flex items-center justify-center rounded-md align-middle ml-1"
            style={{ width: 22, height: 22, background: "rgba(0,229,255,0.1)", border: "1px solid rgba(0,229,255,0.35)" }}
            aria-hidden="true"
          >
            <Share className="w-3 h-3" style={{ color: "#00E5FF" }} strokeWidth={2.2} />
          </span>
        </Step>
        <Step n="2">
          Scroll and tap{" "}
          <span className="text-white inline-flex items-center gap-1">
            <strong>Add to Home Screen</strong>
            <span
              className="inline-flex items-center justify-center rounded-md align-middle"
              style={{ width: 22, height: 22, background: "rgba(57,255,20,0.1)", border: "1px solid rgba(57,255,20,0.35)" }}
              aria-hidden="true"
            >
              <Plus className="w-3 h-3" style={{ color: "#39FF14" }} strokeWidth={2.5} />
            </span>
          </span>
        </Step>
        <Step n="3">
          Tap <strong className="text-white">Add</strong> in the top-right. EVIQ will
          appear on your home screen just like a native app.
        </Step>
      </ol>

      <p className="text-[11px] text-zinc-500 leading-relaxed">
        Only works in <strong className="text-zinc-300">Safari</strong> on iOS. Chrome
        for iOS unfortunately can&rsquo;t install home-screen apps.
      </p>
    </div>
  </div>
);

/** Desktop / unsupported-browser fallback modal — QR to open on phone. */
const DesktopInstallHelp = ({ onClose }) => {
  const url =
    typeof window !== "undefined"
      ? `${window.location.protocol}//${window.location.host}/app`
      : "https://eviq.pro/app";
  return (
    <div
      className="fixed inset-0 z-[110] bg-black/90 backdrop-blur-md flex items-center justify-center px-5"
      data-testid="desktop-install-help"
      role="dialog"
      aria-modal="true"
    >
      <div className="relative w-full max-w-md rounded-3xl border border-white/10 bg-[#0a0a0a] p-6 text-white">
        <button
          onClick={onClose}
          aria-label="Close"
          data-testid="desktop-install-close"
          className="absolute top-3 right-3 text-zinc-500 hover:text-white p-2 rounded-full border border-white/10 hover:border-white/25 transition-colors"
        >
          <X className="w-3.5 h-3.5" strokeWidth={2} aria-hidden="true" />
        </button>

        <div className="flex items-start gap-3 mb-5">
          <div
            className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: "rgba(57,255,20,0.1)", border: "1px solid rgba(57,255,20,0.35)" }}
            aria-hidden="true"
          >
            <QrCode className="w-5 h-5" style={{ color: "#39FF14" }} strokeWidth={2} />
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-1">
              Install without the app store
            </div>
            <h3 className="text-lg font-semibold leading-tight">
              Scan to install EVIQ on your phone
            </h3>
          </div>
        </div>

        <div className="flex flex-col items-center gap-4 py-2">
          <div className="rounded-2xl bg-white p-3">
            <QRCodeSVG
              value={url}
              size={168}
              bgColor="#ffffff"
              fgColor="#000000"
              level="M"
              data-testid="desktop-install-qr"
            />
          </div>
          <div className="text-center">
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-1">
              Or open on your phone
            </div>
            <div className="font-mono text-sm text-white">{url}</div>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3 text-[11px] text-zinc-300">
          <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3">
            <div className="font-semibold text-white mb-1">On iPhone</div>
            Open in <strong>Safari</strong>, tap <strong>Share</strong>, then{" "}
            <strong>Add to Home Screen</strong>.
          </div>
          <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3">
            <div className="font-semibold text-white mb-1">On Android</div>
            Open in <strong>Chrome</strong>, tap the menu, then <strong>Install app</strong>.
          </div>
        </div>

        <p className="mt-4 text-[10px] font-mono text-zinc-500 text-center inline-flex items-center gap-1.5 w-full justify-center">
          <Monitor className="w-3 h-3" aria-hidden="true" />
          Desktop browsers don&rsquo;t install home-screen apps — that&rsquo;s a phone-only feature.
        </p>
      </div>
    </div>
  );
};

const Step = ({ n, children }) => (
  <li className="flex gap-3 items-start rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2.5 text-sm text-zinc-300 leading-relaxed">
    <span
      className="shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-mono font-semibold"
      style={{ background: "rgba(0,229,255,0.12)", color: "#00E5FF", border: "1px solid rgba(0,229,255,0.4)" }}
      aria-hidden="true"
    >
      {n}
    </span>
    <span>{children}</span>
  </li>
);
